/* eslint-disable no-unused-vars */
import { SocketContext } from "../context/SocketContext"

import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from "react-redux";
import { io } from 'socket.io-client';
import { useCompany } from "../hooks/useCompany";
import { useIntegrationWhatsApp } from "../hooks/useIntegrationWhatsApp";
import { api } from "../api/api";
import { sendPrint } from "../reporting/print";
import { ADD_NEW_ORDERS, SET_ORDERS, UPDATE_ORDER } from "../stores/reducers/OrderReducers";
import { ADD_NEW_ORDERS_IFOOD, SET_ORDERS_IFOOD, UPDATE_ORDER_IFOOD } from "../stores/reducers/IfoodOrders";
import { ADD_NEW_COMMAND_TABLE, ADD_NEW_TABLE, CLOSE_TABLE, DELETE_COMMAND_TABLE, SET_TABLES_DATA, UPDATE_COMMAND } from "../stores/reducers/TableReducers";
import { GlobalContext } from "../context/GlobalContext";

export const SocketProvider = ({ children }) => {
  const socketRef = useRef(null)

  if (!socketRef.current) {
    socketRef.current = io(process.env.REACT_APP_BACKEND, {
      autoConnect: false,
      auth: (callback) => callback({ token: localStorage.getItem('access_token') }),
      extraHeaders: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`
      }
    });
  }

  const socket = socketRef.current
  const preloadTimeoutRef = useRef(null)
  const hasPreloadedRef = useRef(false)

  const dispatch = useDispatch()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [connected, setConnected] = useState(socket.connected)

  const { isAccess } = useContext(GlobalContext)

  const { configurations } = useCompany()
  const { sendMessage } = useIntegrationWhatsApp()

  function clearPreloadTimeout() {
    if (preloadTimeoutRef.current) {
      clearTimeout(preloadTimeoutRef.current)
      preloadTimeoutRef.current = null
    }
  }

  function startPreloadTimeout() {
    clearPreloadTimeout()
    preloadTimeoutRef.current = setTimeout(() => {
      if (hasPreloadedRef.current) return
      setLoading(false)
      setError('A conexão demorou mais que o esperado. Verifique sua internet e tente novamente.')
    }, 20000)
  }

  function refreshSocketCredentials() {
    const token = localStorage.getItem('access_token')
    socket.io.opts.extraHeaders = {
      ...socket.io.opts.extraHeaders,
      Authorization: `Bearer ${token}`
    }
  }

  function retry() {
    hasPreloadedRef.current = false
    setError(null)
    setLoading(true)
    startPreloadTimeout()
    refreshSocketCredentials()

    if (socket.connected) {
      socket.emit('request-preload-orders')
      return
    }

    socket.connect()
  }

  useEffect(() => {
    if (!configurations?.id) {
      return
    }

    const handlePreloadOrders = ({ dataOrders, dataOrdersIfood, dataTables }) => {
      if (window.location.pathname === '/' || window.location.pathname === '/register') {
        hasPreloadedRef.current = true
        clearPreloadTimeout()
        setError(null)
        setLoading(false)
        return dispatch(SET_ORDERS([]))
      }

      const orders = Array.isArray(dataOrders) ? dataOrders : []
      const ifoodOrders = Array.isArray(dataOrdersIfood) ? dataOrdersIfood : []
      const tables = Array.isArray(dataTables) ? dataTables : []

      dispatch(SET_ORDERS(orders))
      dispatch(SET_ORDERS_IFOOD(ifoodOrders))

      const notifyOrderPedding = orders.filter((valueItem) => valueItem.status === 'aguardando' && valueItem.typeOrder === 'delivery')

      if (notifyOrderPedding.length > 0) {
        if (configurations?.sound) {
          document.getElementById('audio-new-order')?.play().catch(() => { })
        }
      }

      dispatch(SET_TABLES_DATA(tables))

      hasPreloadedRef.current = true
      clearPreloadTimeout()
      setError(null)
      setLoading(false)
    }

    const handlePreloadError = (payload) => {
      clearPreloadTimeout()
      setLoading(false)
      setError(payload?.message || 'Não foi possível carregar os dados iniciais.')
    }

    socket.on('preload-orders', handlePreloadOrders)
    socket.on('preload-orders-error', handlePreloadError)

    return () => {
      socket.off('preload-orders', handlePreloadOrders)
      socket.off('preload-orders-error', handlePreloadError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurations]);

  useEffect(() => {
    if (!configurations?.id) {
      return
    }

    socket.on('new-order', async (order) => {
      if (isAccess() === 'garcom') {
        return;
      }

      if (configurations?.sound && order.typeOrder === 'delivery') {
        document.getElementById('audio-new-order').play()
      }

      dispatch(ADD_NEW_ORDERS(order))
    });

    socket.on('new-order-ifood', async (order) => {
      if (isAccess() === 'garcom') {
        return;
      }

      if (configurations?.sound) {
        document.getElementById('audio-new-order').play()
      }

      if (configurations?.autoPrintOrder || configurations?.autoPrintIfood) {
        await api.get('/ifood/print?id=' + order.id).catch(console.log)
      }

      dispatch(ADD_NEW_ORDERS_IFOOD(order))
    });

    socket.on('new-order-updated', async (order) => {
      console.log(order)
      dispatch(UPDATE_ORDER(order))
    })

    socket.on('new-order-ifood-updated', async (order) => {
      dispatch(UPDATE_ORDER_IFOOD(order))
    })

    socket.on('new-command', async (command) => {
      dispatch(ADD_NEW_COMMAND_TABLE(command))
    })

    socket.on('new-table', async (table) => {
      dispatch(ADD_NEW_TABLE(table))
    })

    socket.on('delete-command', async (command) => {
      dispatch(DELETE_COMMAND_TABLE(command.idCommand))
    })

    socket.on('close-table', async (table) => {
      dispatch(CLOSE_TABLE(table.tableId))
    })

    socket.on('updated-command', async (command) => {
      dispatch(UPDATE_COMMAND(command))
    })

    return () => {
      socket.off('new-order');
      socket.off('new-order-ifood');
      socket.off('new-order-updated');
      socket.off('new-order-ifood-updated');
      socket.off('new-command');
      socket.off('delete-command');
      socket.off('close-table');
      socket.off('updated-command');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurations])

  useEffect(() => {
    if (!configurations?.id) {
      if (window.location.pathname !== '/' && window.location.pathname !== '/register') {
        startPreloadTimeout()
      }
      return clearPreloadTimeout
    }

    const handleConnect = () => {
      setConnected(true)
      if (hasPreloadedRef.current) return
      setError(null)
      setLoading(true)
      startPreloadTimeout()
    }

    const handleConnectError = (connectionError) => {
      setConnected(false)
      if (hasPreloadedRef.current) return
      clearPreloadTimeout()
      setLoading(false)
      setError(connectionError?.message || 'Não foi possível conectar ao servidor em tempo real.')
    }

    const handleDisconnect = () => {
      setConnected(false)
      if (hasPreloadedRef.current) return
      clearPreloadTimeout()
      setLoading(false)
      setError('A conexão com o servidor foi interrompida. Tente novamente.')
    }

    socket.on('connect', handleConnect)
    socket.on('connect_error', handleConnectError)
    socket.on('disconnect', handleDisconnect)

    startPreloadTimeout()
    refreshSocketCredentials()
    socket.connect()

    return () => {
      clearPreloadTimeout()
      socket.off('connect', handleConnect)
      socket.off('connect_error', handleConnectError)
      socket.off('disconnect', handleDisconnect)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurations,])

  useEffect(() => {
    return () => {
      clearPreloadTimeout()
      socket.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <SocketContext.Provider value={{ loading, error, retry, connected, socket }}>
      {children}
    </SocketContext.Provider>
  )
}
